/**
 * confirmation-sections.ts — the section registry behind a journey's
 * confirmation page (2026-08-31).
 *
 * NAMING (2026-09-01, agreed with the API line). Two features share the word
 * "confirmation page" and it cost three days. This is the **JOURNEY END PAGE**:
 * the last screen INSIDE the booking flow, a locked node before the End. The
 * other one is the **BOOKING CONFIRMATION PAGE** — reached from the link in a
 * confirmation email at manage.<tenant>/<booking id>, opened days or months
 * later, designed PER SERVICE, and authored in ConfirmationPages.tsx beside
 * this file. They are unrelated in the data model; the only connection is that
 * a journey end page can carry a link to the booking one. If you are looking
 * for service claiming, stealing or a tenant default, you are in the wrong
 * file — that is the other feature, and it is correct there.
 *
 * Sections are NOT blocks, and the distinction is the reason this file exists.
 * A block holds copy an author typed; a section renders data that only exists at
 * runtime — the booked service, the reference, the answers given. An author
 * orders them, hides them and configures a few fields; they cannot write their
 * content, because the content is the booking.
 *
 * Ported from the tenant-level confirmation-pages library, with three changes
 * ruled by the operator on 2026-08-31:
 *
 *   · BRANDING IS THE JOURNEY'S. `CPBranding` — inherit / brandColor / pageBg —
 *     is gone. A confirmation page takes its journey's theme like every other
 *     page in it, which is also the only way a customer reaching it from a
 *     booking flow sees one continuous design instead of two that nearly match.
 *   · SERVICES ARE PASSED IN, NOT CLAIMED. The old model assigned services to
 *     pages (`serviceId → pageId`, exclusive, with a tenant default to catch the
 *     rest). A journey already knows its services and the booking arrives
 *     carrying the one that was picked, so there is nothing to assign — and the
 *     per-service/per-journey scope conflict never arises.
 *   · APPLICABILITY MOVED FROM THE PAGE TO THE SECTION. A whole page used to
 *     declare `applies: "all" | "appointment" | "event" | "queue"`. That fitted a
 *     tenant template picked by service. On a journey page it is the SECTIONS
 *     that vary — Guests means nothing to a queue, a meeting link means nothing
 *     to an in-person appointment — so the flag dropped a level and the rail
 *     offers a journey only what its type can use.
 */

/** Journey types a confirmation section can appear on. */
export type CSType = "queue" | "appointment" | "event";
export const CS_ALL_TYPES: CSType[] = ["queue", "appointment", "event"];

export interface ConfirmationSection {
  id: string;
  label: string;
  glyph: string;
  tint: string;
  color: string;
  /** Journey types this section means anything on. */
  types: CSType[];
  /** Renders only on a page the customer opens AFTER the booking — the booking
   *  confirmation page reached from the emailed link, never the journey end
   *  page shown seconds after booking. This is the one axis that is not journey
   *  type: a feedback prompt cannot ask how it went while the customer is still
   *  standing in the flow. Absent means it renders on both surfaces. */
  afterTheFact?: boolean;
  /** A locked section cannot be hidden or removed. */
  locked?: boolean;
  /** Present on a new page but switched off — it competes with the manage
   *  actions and should be a deliberate choice. */
  offByDefault?: boolean;
  /** What an author sees when the section is selected. */
  help: string;
  /** Page-level fields this section reads, edited in the inspector. */
  fields?: string[];
}

export const CONFIRMATION_SECTIONS: ConfirmationSection[] = [
  { id: "brandHeader", label: "Brand header", glyph: "◈", tint: "#EDE5FE", color: "#5142C7", types: CS_ALL_TYPES,
    help: "The tenant logo and name, in this journey's branding. Shown on new pages by default; hide or remove it when the surrounding customer surface already carries the brand." },
  { id: "banner", label: "Confirmation banner", glyph: "✓", tint: "#E3F5E9", color: "#1D8348", types: CS_ALL_TYPES,
    fields: ["bannerTitle", "bannerSub"],
    help: "The headline that states what happened. Its wording and colour follow the outcome — confirmed, pending approval, waitlisted, no-show — so one page serves every ending of the flow." },
  { id: "summary", label: "Booking summary", glyph: "▤", tint: "#E8EEFD", color: "#2D60EB", types: CS_ALL_TYPES,
    fields: ["summaryDateFormat", "summaryTimeFormat", "summaryOff"],
    help: "What was booked, reading whichever fields this journey type has. An appointment shows service, date, time, duration and advisor; a queue shows the service and ticket; an event shows the event name, date and venue. Without it a registrant lands on a page that confirms something happened without naming it." },
  { id: "place", label: "Staff, location & branch", glyph: "◉", tint: "#E0F2F7", color: "#0E7490", types: CS_ALL_TYPES,
    help: "Who the customer is seeing and where, with the address. Hidden automatically when the booking is phone or video — there is no place to show. The map is a static tile, not an embedded interactive one." },
  { id: "meeting", label: "Meeting type", glyph: "▷", tint: "#EDE5FE", color: "#5142C7", types: ["appointment", "event"],
    help: "In person, phone or video — and the join link once a video booking has one. Only ever renders what the booking actually is, so it is safe to leave on." },
  { id: "guests", label: "Guests & participants", glyph: "◍", tint: "#FDEBF3", color: "#D61A55", types: ["event"],
    help: "The additional attendees captured at registration. Events only — a queue and an appointment book one person." },
  { id: "answers", label: "Answers", glyph: "?", tint: "#F6F2FE", color: "#5142C7", types: CS_ALL_TYPES,
    help: "What the customer answered. On a queue or appointment the questions came from the SERVICE they picked, so the designer previews them against a sample service; on an event they came from the event's own group, so the preview is exact. Hide it when answers hold anything you would not want re-displayed on a link a customer might forward." },
  { id: "reference", label: "Reference number", glyph: "#", tint: "#F4F5F7", color: "#697180", types: CS_ALL_TYPES,
    help: "The booking reference or queue ticket number, shown large — it is what a customer reads out on the phone." },
  /* The check-in code. Its own section rather than a switch inside Downloads:
     a download is a file the customer takes away, and this is a thing they hold
     up at a desk — different moment, different place on the page, and a tenant
     that wants one rarely wants the other. Off by default because it only earns
     its space where staff actually scan something. The symbol is encoded in the
     browser from the booking's own reference (lib/qrcode), so it needs no
     operation and no image to be served. */
  { id: "checkinCode", label: "Check-in code", glyph: "▦", tint: "#EDE5FE", color: "#5142C7", types: CS_ALL_TYPES, offByDefault: true,
    fields: ["checkinCodeTarget"],
    help: "A scannable code carrying the booking reference, for a desk or kiosk that scans on arrival. Switch it on only where something actually scans it — a code nobody reads is a box of noise on a phone screen." },
  { id: "downloads", label: "Downloads", glyph: "↓", tint: "#E4F5EF", color: "#1F8A5B", types: ["appointment", "event"],
    help: "Calendar file, wallet pass and PDF summary, each independently switchable. A queue place is too short-lived to be worth a calendar entry." },
  { id: "manage", label: "Manage actions", glyph: "⟳", tint: "#FFFAED", color: "#916D0F", types: ["appointment", "event"],
    fields: ["cancelHours", "rebookHours"],
    help: "Reschedule and cancel, each bounded by its own window below. Moving and cancelling cost the branch different things, so they need not close at the same time. A queue offers leave-the-queue on the live ticket instead." },
  { id: "another", label: "Book another", glyph: "+", tint: "#E8EEFD", color: "#2D60EB", types: CS_ALL_TYPES,
    help: "A link back into this journey." },
  { id: "promo", label: "Cross-sell card", glyph: "◆", tint: "#EDE5FE", color: "#5142C7", types: CS_ALL_TYPES, offByDefault: true,
    help: "One promoted service or event. Off by default — it competes with the manage actions for the same attention." },
  /* 2026-09-07, operator: the customer sees EXACTLY the authored page, so the
     platform's own "Your details" card is gone and this section is where those
     details now live. Off by default — a page that prints the customer's name
     and email is a choice an admin makes deliberately, and the emailed link can
     be forwarded. On a masking tenant the values render masked and the reveal
     runs the manage surface's own step-up; on the common tenant (masking
     defaults FALSE) they render in the clear. */
  { id: "yourDetails", label: "Your details", glyph: "◑", tint: "#E8EEFD", color: "#2D60EB", types: CS_ALL_TYPES, offByDefault: true,
    help: "The customer's own name and email, as the booking holds them. Off by default: a confirmation link can be forwarded, so printing personal details is a deliberate choice. On a tenant with masking on the values are masked until the customer completes the reveal step — the same step-up the manage surface uses." },
  { id: "footer", label: "Custom note & footer", glyph: "▁", tint: "#F4F5F7", color: "#697180", types: CS_ALL_TYPES,
    fields: ["note"],
    help: "Tenant address, contact and policy links, plus any note you add." },
  /* Merged in from the booking confirmation page's own registry (2026-09-02,
     convergence step 2). Two registries described the same thirteen sections in
     different words and then diverged by three; one registry is the only way
     one rail can render both pages. */
  { id: "prepare", label: "What to bring", glyph: "☑", tint: "#FDF0E3", color: "#B9770E", types: CS_ALL_TYPES,
    fields: ["prepare"],
    help: "A short checklist — ID, documents, what to wear — rendered above the footer. One line per item, written below." },
  { id: "queuePos", label: "Live queue position", glyph: "▮", tint: "#E8F1FD", color: "#1B58B8", types: ["queue"],
    help: "Place in line and estimated wait, live. Queue tickets only — nothing else has a position." },
  { id: "survey", label: "Feedback prompt", glyph: "☆", tint: "#FDF0E3", color: "#B9770E", types: CS_ALL_TYPES, afterTheFact: true,
    help: "Invites a response once the booking has passed. Only ever renders on a page opened after the fact — it cannot ask how it went while the customer is still in the flow." },
];

export const csDef = (id: string): ConfirmationSection | null =>
  CONFIRMATION_SECTIONS.filter((s) => s.id === id)[0] ?? null;

/** Sections a journey of this type may use, in rail order.
 *
 *  `surface` (2026-09-02) is what keeps ONE registry serving both designers:
 *  "end" is the journey end page, shown seconds after booking; "linked" is the
 *  booking confirmation page, opened from the emailed link days later. Sections
 *  marked `afterTheFact` exist only on the second. Defaults to "end" so the
 *  journey designer's existing calls are unchanged. */
export const csForType = (type: CSType, surface: CSSurface = "end"): ConfirmationSection[] =>
  CONFIRMATION_SECTIONS.filter((s) => s.types.indexOf(type) > -1 && (surface === "linked" || !s.afterTheFact));

export type CSSurface = "end" | "linked";

/** One section as stored on a page. Array position IS render order and `hidden`
 *  sits on the section itself.
 *
 *  Was `{ order: string[]; off: Record<string, boolean> }` until 2026-09-01. The
 *  API line's shape replaced it, and their argument is the right one: an
 *  `order[]` beside an `off{}` is two structures describing one fact, and they
 *  can disagree — a section in `off` that is missing from `order`, or the
 *  reverse. A third of `csMigrate` used to exist to defend against states this
 *  shape cannot express. It is also the shape the contract types, which is what
 *  lets one rail and one canvas render both this page and the booking
 *  confirmation page instead of two implementations that agree by convention.
 *
 *  `uid` is stable per row and survives reordering; `type` is the registry id.
 *  An unknown `type` is preserved and ignored, never dropped — that is what lets
 *  a section reach a designer before this file knows about it. */
export interface CSSection { uid: string; type: string; hidden?: boolean; [k: string]: unknown }
export type CSState = CSSection[];

/** A row's uid. DETERMINISTIC — `cs-<type>` — and that is load-bearing, not
 *  cosmetic. `csMigrate` runs on every render, so a counter-based uid minted a
 *  fresh identity each pass: a selected section would deselect itself, and any
 *  effect keyed on the section list would re-run forever. A registry section
 *  appears at most once on a page (`csMissing` enforces it), so the type is
 *  already a unique key; a stored uid is always preferred over a minted one, so
 *  documents that carry their own survive untouched. */
export const csUid = (type: string): string => `cs-${type}`;

/** Index of a section by uid, or -1. */
export const csIndex = (s: CSState, uid: string): number => s.findIndex((x) => x.uid === uid);
/** The row for a uid, or null. */
export const csAt = (s: CSState, uid: string): CSSection | null => s.find((x) => x.uid === uid) ?? null;

/** A new confirmation page for this journey type: every section its type can
 *  use, in registry order, with the off-by-default ones switched off. */
export function csDefaultState(type: CSType, surface: CSSurface = "end"): CSState {
  return csForType(type, surface).map((d) => ({ uid: csUid(d.id), type: d.id, ...(d.offByDefault ? { hidden: true } : {}) }));
}

/** Read whatever is stored, defensively. Handles three inputs: the array shape,
 *  the legacy `{order, off}` object (pages saved before 2026-09-01), and
 *  nothing at all. Idempotent.
 *
 *  Unknown `type` values are KEPT, with their config, and left for the renderer
 *  to pass through — the registry not knowing a section is not evidence the
 *  section is wrong. Sections whose type this journey type cannot use are also
 *  kept: the rail greys them rather than deleting an author's work because a
 *  journey was retyped. Only genuinely malformed rows are dropped. */
export function csMigrate(raw: unknown, type: CSType, surface: CSSurface = "end"): CSState {
  const legacy = raw as { order?: unknown; off?: unknown } | null | undefined;
  let rows: CSSection[];

  if (Array.isArray(raw)) {
    rows = (raw as unknown[]).filter((r): r is CSSection =>
      !!r && typeof r === "object" && typeof (r as CSSection).type === "string")
      .map((r) => ({ ...r, uid: typeof r.uid === "string" && r.uid ? r.uid : csUid(r.type) }));
  } else if (legacy && Array.isArray(legacy.order)) {
    const off = (legacy.off ?? {}) as Record<string, boolean>;
    rows = (legacy.order as unknown[]).filter((id): id is string => typeof id === "string")
      .map((id) => ({ uid: csUid(id), type: id, ...(off[id] ? { hidden: true } : {}) }));
  } else {
    return csDefaultState(type, surface);
  }

  /* An explicit stored array is the authored document, including an empty one.
     Defaults apply only when the field is absent or has an unsupported top-level
     shape; migration preserves hidden flags, removals, ordering and unknown rows. */
  return rows;
}

/* ── state operations ─────────────────────────────────────────────────────
   Pure, keyed by UID rather than section id, and all of them no-ops rather than
   throws on a locked or unknown row: the rail hides those controls, but a stored
   document is not trusted to. */

/** Reorder. `to` is the index the section lands on in the new list. */
export function csMove(s: CSState, from: number, to: number): CSState {
  if (from === to || from < 0 || from >= s.length || to < 0 || to >= s.length) return s;
  const next = s.slice();
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row!);
  return next;
}

/** Show ⇄ hide. A hidden section keeps its position, so switching it back on
 *  returns it to where the author put it. */
export function csToggle(s: CSState, uid: string): CSState {
  const row = csAt(s, uid);
  if (!row || csDef(row.type)?.locked) return s;
  return s.map((r) => (r.uid === uid ? { ...r, hidden: !r.hidden } : r));
}

/** Take it off the page entirely. */
export function csRemove(s: CSState, uid: string): CSState {
  const row = csAt(s, uid);
  if (!row || csDef(row.type)?.locked) return s;
  return s.filter((r) => r.uid !== uid);
}

/** Put one on, at the end, switched on. Returns the new state and the uid it
 *  minted, so a caller can select what it just added. */
export function csAdd(s: CSState, type: string): { state: CSState; uid: string } {
  if (!csDef(type)) return { state: s, uid: "" };
  const uid = csUid(type);
  return { state: s.concat({ uid, type }), uid };
}

/** Patch one row's own config — a section storing a field on itself rather than
 *  on the page. `uid` and `type` are not patchable: they are identity. */
export function csPatch(s: CSState, uid: string, patch: Record<string, unknown>): CSState {
  return s.map((r) => (r.uid === uid ? { ...r, ...patch, uid: r.uid, type: r.type } : r));
}

/** Sections this journey type can use that are not on the page — the rail's
 *  add row. A registry section appears at most once, so this is by type. */
export const csMissing = (s: CSState, type: CSType, surface: CSSurface = "end"): ConfirmationSection[] =>
  csForType(type, surface).filter((d) => !s.some((r) => r.type === d.id));

/** Does this section render for a journey of this type? Used by the rail to
 *  grey what cannot apply, rather than hiding it and leaving an author
 *  wondering where Guests went. */
export const csApplies = (id: string, type: CSType, surface: CSSurface = "end"): boolean => {
  const d = csDef(id);
  return !!d && d.types.indexOf(type) > -1 && (surface === "linked" || !d.afterTheFact);
};

/** The journey type a document's `type` string maps onto. */
export const csTypeOf = (docType: string | undefined): CSType =>
  docType === "Event" ? "event" : docType === "Appointment" ? "appointment" : "queue";
